import React, { useContext } from 'react';
import { createPortal } from 'react-dom';
import { DragOverlay, DropAnimation } from '@dnd-kit/core';
import DraggableOverlay from './DraggableOverlay';
import { Event } from '../../types';
import { useRecoilState, useSetRecoilState } from 'recoil';
import { dialogActiveAtom, shouldShowPopoverDialogAtom } from '../../atoms';
import { EditAppointmentType } from 'RestAPIs/activities/types';
import { PopOverConfirmationContext } from '../../PopOverConfirmationContext';
import dayjs from 'dayjs';
import { PopoverWarningDialog } from '..';
import { Box } from '@mui/material';
import { AppointmentChipOverlay } from '../../TimelineView/AppointmentChip';
import useTimelineInterval from '../../hooks/useTimelineInterval';
import { MultiDragOverlay } from '../../TimelineView/MultiDragOverlay';

type DragPortalProps = {
  activeEvent: Event | null;
  selectedIds: string[];
  pendingDropPosition: { x: number; y: number } | null;
  dropAnimation: DropAnimation;
  isTimeline?: boolean;
  timelineRouteWidth?: number;
  interval?: ReturnType<typeof useTimelineInterval>;
  mousePosition?: { x: number; y: number };
  dragParams?: { x: number; y: number; isToPlan: boolean };
};

const DragPortal: React.FC<DragPortalProps> = ({
  activeEvent,
  selectedIds,
  pendingDropPosition,
  dropAnimation,
  isTimeline = false,
  timelineRouteWidth = 0,
  interval = { percentile: 0 },
  mousePosition = { x: 0, y: 0 },
  dragParams = { x: 0, y: 0, isToPlan: false }
}) => {
  if (!activeEvent) return null;

  const [
    {
      id: popoverOccurenceId,
      isUnassignment,
      scheduleStart: popoverScheduleStart
    },
    setShouldShowPopoverDialog
  ] = useRecoilState(shouldShowPopoverDialogAtom);

  const { isLoading, onClose, onHandleAppointmentConfirmation } = useContext(
    PopOverConfirmationContext
  );

  const hasStart = !!(popoverScheduleStart && activeEvent?.start);
  const duration =
    (activeEvent?.durationInSeconds || 0) / interval?.percentile || 0;
  const widthInPixels = (duration / 100) * timelineRouteWidth;
  const setDialogActive = useSetRecoilState(dialogActiveAtom);

  // We compare only the YYYY-MM-DD of the dates, not the time.
  const popoverScheduleStartDate: string = dayjs(popoverScheduleStart).isValid()
    ? dayjs(popoverScheduleStart).format('YYYY-MM-DD')
    : '';
  const currentScheduleStartDate: string = dayjs(activeEvent?.start).isValid()
    ? dayjs(activeEvent?.start).format('YYYY-MM-DD')
    : '';

  const isPopOverId = popoverOccurenceId === activeEvent?.id;

  const showPopoverDialog: boolean =
    activeEvent?.isRecurring &&
    (isPopOverId ||
      popoverOccurenceId === 'IS_MULTIPLE' ||
      (isPopOverId &&
        hasStart &&
        popoverScheduleStartDate === currentScheduleStartDate));

  return createPortal(
    <>
      {!pendingDropPosition && (
        <DragOverlay
          wrapperElement="ul"
          dropAnimation={dropAnimation}
          zIndex={99999}
        >
          {activeEvent &&
            isTimeline &&
            (selectedIds.length > 1 ? (
              <MultiDragOverlay
                id={activeEvent.id}
                hasTransformedSize={!activeEvent.appointmentGrouping}
                count={selectedIds.length}
                mousePosition={mousePosition}
              />
            ) : (
              <AppointmentChipOverlay
                {...activeEvent}
                selected={selectedIds.includes(activeEvent.id)}
                mousePosition={mousePosition}
                width={widthInPixels}
                droppable={true}
              />
            ))}
          {activeEvent && !isTimeline && (
            <DraggableOverlay
              {...activeEvent}
              activeEvent={activeEvent}
              count={selectedIds.length}
              amountSelected={selectedIds.length}
            />
          )}
        </DragOverlay>
      )}
      {activeEvent && pendingDropPosition && (
        <PopoverWarningDialog
          open={showPopoverDialog && !isLoading}
          isMultipleSelected={selectedIds.length > 1}
          onClose={() => {
            onClose?.();
            setShouldShowPopoverDialog({ id: '' });
            setDialogActive(false);
          }}
          onClickAppointment={(editAppointmentType: EditAppointmentType) => {
            onHandleAppointmentConfirmation?.(
              editAppointmentType,
              isUnassignment
            );
            setShouldShowPopoverDialog({ id: '' });
          }}
        >
          <Box
            sx={{
              position: 'absolute',
              top:
                isTimeline && !dragParams.isToPlan
                  ? pendingDropPosition?.y - dragParams?.y
                  : pendingDropPosition?.y,
              left:
                isTimeline && !dragParams.isToPlan
                  ? pendingDropPosition?.x - dragParams?.x
                  : pendingDropPosition?.x,
              zIndex: 99
            }}
          >
            {isTimeline ? (
              selectedIds.length > 1 ? (
                <MultiDragOverlay
                  id={activeEvent.id}
                  hasTransformedSize={!activeEvent.appointmentGrouping}
                  count={selectedIds.length}
                  mousePosition={mousePosition}
                  isDroppedOverlay={true}
                />
              ) : (
                <AppointmentChipOverlay
                  {...activeEvent}
                  selected={selectedIds.includes(activeEvent.id)}
                  mousePosition={mousePosition}
                  width={widthInPixels}
                  droppable={true}
                  isDroppedOverlay={true}
                />
              )
            ) : (
              <DraggableOverlay
                {...activeEvent}
                activeEvent={activeEvent}
                count={selectedIds.length}
                mousePosition={pendingDropPosition}
                amountSelected={selectedIds.length}
              />
            )}
          </Box>
        </PopoverWarningDialog>
      )}
    </>,
    document.body
  );
};

export default DragPortal;
